import {
    computed, observable, makeObservable,
} from 'mobx';
import { Axios } from '../utilities/network';
import { isWeb } from '../utilities/platform';
import RootStore from './root-store';
import { GetLocalStorage, SetLocalStorage } from '../utilities/cryptography';
import { USER_INFO } from '../constants/storage-keys';
import { navigate } from 'gatsby';
import { CURRENT_PLATFORM } from '../constants/settings';
import { ClinicMemberStatus, Gender, PAGE_SIZE } from '../constants/options';
import { ProfileTemplate } from '../utilities/profile';
import { GetApiLang, GetLangKey, GetUserProfile, IsWorkingInClinic, Language } from '../utilities/general';

export default class LibraryStore {

    loading: boolean = false;

    //   loadingHistory: boolean = false;

    creating: boolean = false;

    updating: boolean = false;

    deleting: boolean = false;

    rootStore: RootStore;

    constructor(rootStore: RootStore) {
        makeObservable(this, {
            loading: observable,
            creating: observable,
            updating: observable,
        });
        this.rootStore = rootStore;
    }

    getBookById = async (id: number, lang: Language = GetApiLang()) => {
        this.loading = true;
        try {
            const res = await Axios.get(`/api/Book/${id}?lang=${lang}&platformId=${CURRENT_PLATFORM}`);
            return Promise.resolve(res.data);
        } catch (err) {
            return Promise.reject(err);
        } finally {
            this.loading = false;
        }
    };

    searchBooks = async (skip: number, take: number = PAGE_SIZE, reqModel: any = {}, lang: Language = GetApiLang()) => {
        this.loading = true;
        try {
            const res = await Axios.post(`/api/Book/Search/${skip}/${take}?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
            return Promise.resolve(res.data);
        } catch (err) {
            return Promise.reject(err);
        } finally {
            this.loading = false;
        }
    };

    getBookMemberSavedById = async (id: number, lang: Language = GetApiLang()) => {
        this.loading = true;
        try {
            const res = await Axios.get(`/api/Book/Member/Saved/${id}?lang=${lang}&platformId=${CURRENT_PLATFORM}`);
            return Promise.resolve(res.data);
        } catch (err) {
            return Promise.reject(err);
        } finally {
            this.loading = false;
        }
    };

    deleteBookMemberSavedById = async (id: number, lang: Language = GetApiLang()) => {
        this.deleting = true;
        try {
            await Axios.delete(`/api/Book/Member/Saved/${id}?lang=${lang}&platformId=${CURRENT_PLATFORM}`);
            return Promise.resolve();
        } catch (err) {
            return Promise.reject(err);
        } finally {
            this.deleting = false;
        }
    };

    createBookMemberSaved = async (reqModel: any, lang: Language = GetApiLang()) => {
        this.creating = true;
        try {
            const res = await Axios.post(`/api/Book/Member/Saved?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
            return Promise.resolve(res.data);
        } catch (err) {
            return Promise.reject(err);
        } finally {
            this.creating = false;
        }
    };

    searchBookMemberSaved = async (skip: number, take: number = PAGE_SIZE, reqModel: any = {}, lang: Language = GetApiLang()) => {
        this.loading = true;
        try {
            const res = await Axios.post(`/api/Book/Member/Saved/Search/${skip}/${take}?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
            return Promise.resolve(res.data);
        } catch (err) {
            return Promise.reject(err);
        } finally {
            this.loading = false;
        }
    };

    getBookMemberRecentById = async (id: number, lang: Language = GetApiLang()) => {
        this.loading = true;
        try {
            const res = await Axios.get(`/api/Book/Member/Recent/${id}?lang=${lang}&platformId=${CURRENT_PLATFORM}`);
            return Promise.resolve(res.data);
        } catch (err) {
            return Promise.reject(err);
        } finally {
            this.loading = false;
        }
    };

    deleteBookMemberRecentById = async (id: number, lang: Language = GetApiLang()) => {
        this.deleting = true;
        try {
            await Axios.delete(`/api/Book/Member/Recent/${id}?lang=${lang}&platformId=${CURRENT_PLATFORM}`);
            return Promise.resolve();
        } catch (err) {
            return Promise.reject(err);
        } finally {
            this.deleting = false;
        }
    };

    createBookMemberRecent = async (reqModel: any, lang: Language = GetApiLang()) => {
        this.creating = true;
        try {
            const res = await Axios.post(`/api/Book/Member/Recent?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
            return Promise.resolve(res.data);
        } catch (err) {
            return Promise.reject(err);
        } finally {
            this.creating = false;
        }
    };

    searchBookMemberRecent = async (skip: number, take: number = PAGE_SIZE, reqModel: any = {}, lang: Language = GetApiLang()) => {
        this.loading = true;
        try {
            const res = await Axios.post(`/api/Book/Member/Recent/Search/${skip}/${take}?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
            return Promise.resolve(res.data);
        } catch (err) {
            return Promise.reject(err);
        } finally {
            this.loading = false;
        }
    };

    getBookChapterById = async (id: number, lang: Language = GetApiLang()) => {
        this.loading = true;
        try {
            const res = await Axios.get(`/api/Book/Chapter/${id}?lang=${lang}&platformId=${CURRENT_PLATFORM}`);
            return Promise.resolve(res.data);
        } catch (err) {
            return Promise.reject(err);
        } finally {
            this.loading = false;
        }
    };

    getBookTagById = async (id: number, lang: Language = GetApiLang()) => {
        this.loading = true;
        try {
            const res = await Axios.get(`/api/Book/Tag/${id}?lang=${lang}&platformId=${CURRENT_PLATFORM}`);
            return Promise.resolve(res.data);
        } catch (err) {
            return Promise.reject(err);
        } finally {
            this.loading = false;
        }
    };

    searchBookTags = async (skip: number, take: number = PAGE_SIZE, reqModel: any = {}, lang: Language = GetApiLang()) => {
        this.loading = true;
        try {
            const res = await Axios.post(`/api/Book/Tag/Search/${skip}/${take}?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
            return Promise.resolve(res.data);
        } catch (err) {
            return Promise.reject(err);
        } finally {
            this.loading = false;
        }
    };

    // searchClinics = async (skip: number, take: number = PAGE_SIZE, reqModel: any = {}, lang: Language = GetApiLang()) => {
    //     this.loading = true;
    //     try {
    //         const res = await Axios.post(`/api/Clinic/Search/${skip}/${take}?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
    //         return Promise.resolve(res.data);
    //     } catch (err) {
    //         return Promise.reject(err);
    //     } finally {
    //         this.loading = false;
    //     }
    // };

    // addClinic = async (reqModel: any, lang: Language = GetApiLang()) => {
    //     this.creating = true;
    //     try {
    //         const res = await Axios.post(`/api/Clinic?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
    //         return Promise.resolve(res.data);
    //     } catch (err) {
    //         return Promise.reject(err);
    //     } finally {
    //         this.creating = false;
    //     }
    // };

    // getClinicById = async (id: number, lang: Language = GetApiLang()) => {
    //     const userInfo = GetUserProfile();
    //     this.loading = true;
    //     try {
    //         const res = await Axios.get(`/api/Clinic/${id}?lang=${lang}&platformId=${CURRENT_PLATFORM}`);
    //         let clinic = res.data;
    //         console.log("userinfo",userInfo)
    //         if (IsWorkingInClinic(clinic.clinicMemberStatus) && !userInfo.availableClinics.find((c: any) => c.id == clinic.id)) {
    //             userInfo.availableClinics.push({ id: clinic.id, name: clinic.name, address: clinic.address });
    //             if (userInfo.currentClinicId == 0) userInfo.currentClinicId = clinic.id;
    //             SetLocalStorage(USER_INFO, userInfo);
    //         } else {
    //             if (!IsWorkingInClinic(clinic.clinicMemberStatus)) {
    //                 userInfo.availableClinics = userInfo.availableClinics.filter((c: any) => c.id != clinic.id);
    //                 console.log(userInfo.availableClinics);
    //                 userInfo.currentClinicId = userInfo.availableClinics[0] ? userInfo.availableClinics[0].id : 0;
    //                 SetLocalStorage(USER_INFO, userInfo);
    //             } else if (userInfo.currentClinicId == 0) {
    //                 userInfo.currentClinicId = clinic.id;
    //                 SetLocalStorage(USER_INFO, userInfo);
    //             }
    //         }
    //         return Promise.resolve(res.data);
    //     } catch (err) {
    //         return Promise.reject(err);
    //     } finally {
    //         this.loading = false;
    //     }
    // };

    // updateClinic = async (reqModel: any, lang: Language = GetApiLang()) => {
    //     this.updating = true;
    //     try {
    //         const res = await Axios.put(`/api/Clinic?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
    //         return Promise.resolve(res.data);
    //     } catch (err) {
    //         return Promise.reject(err);
    //     } finally {
    //         this.updating = false;
    //     }
    // };

    // getGeneralTimeSlots = async (reqModel: any, lang: Language = GetApiLang()) => {
    //     this.loading = true;
    //     try {
    //         const res = await Axios.post(`/api/Clinic/Practitioner/GeneralTimeSlots?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
    //         return Promise.resolve(res.data);
    //     } catch (err) {
    //         return Promise.reject(err);
    //     } finally {
    //         this.loading = false;
    //     }
    // };

    // updateClinicMemberSettings = async (reqModel: any, lang: Language = GetApiLang()) => {
    //     this.updating = true;
    //     try {
    //         const res = await Axios.put(`/api/Clinic/Practitioner/WorkingSetting?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
    //         return Promise.resolve(res.data);
    //     } catch (err) {
    //         return Promise.reject(err);
    //     } finally {
    //         this.updating = false;
    //     }
    // };

    // searchClinicPractitioners = async (skip: number, take: number = PAGE_SIZE, reqModel: any = {}, lang: Language = GetApiLang()) => {
    //     this.searchingPractitioners = true;
    //     try {
    //         const res = await Axios.post(`/api/Clinic/Practitioner/Search/${skip}/${take}?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
    //         return Promise.resolve(res.data);
    //     } catch (err) {
    //         return Promise.reject(err);
    //     } finally {
    //         this.searchingPractitioners = false;
    //     }
    // };

    // invitePractitionerToClinic = async (reqModel: any) => {
    //     this.creating = true;
    //     try {
    //         const res = await Axios.post(`/api/Clinic/Practitioner/Invite?platformId=${CURRENT_PLATFORM}`, reqModel);
    //         return Promise.resolve(res.data);
    //     } catch (err) {
    //         return Promise.reject(err);
    //     } finally {
    //         this.creating = false;
    //     }
    // };

    // multiInvitePractitionerToClinic = async (reqModel: any) => {
    //     this.creating = true;
    //     try {
    //         const res = await Axios.post(`/api/Clinic/Practitioner/MultipleInvite/?platformId=${CURRENT_PLATFORM}`, reqModel);
    //         return Promise.resolve(res.data);
    //     } catch (err) {
    //         return Promise.reject(err);
    //     } finally {
    //         this.creating = false;
    //     }
    // };

    // responseInvitePractitionerToClinic = async (reqModel: any, isApprove: boolean) => {
    //     this.updating = true;
    //     try {
    //         let apiUrl = isApprove ? `/api/Clinic/Practitioner/Approve?platformId=${CURRENT_PLATFORM}`
    //             : `/api/Clinic/Practitioner/Reject?platformId=${CURRENT_PLATFORM}`;
    //         const res = await Axios.put(apiUrl, reqModel);
    //         return Promise.resolve(res.data);
    //     } catch (err) {
    //         return Promise.reject(err);
    //     } finally {
    //         this.updating = false;
    //     }
    // };

    // getClinicMemberById = async (id: number, lang: Language = GetApiLang()) => {
    //     this.loading = true;
    //     try {
    //         const res = await Axios.get(`/api/Clinic/Practitioner/${id}?lang=${lang}&platformId=${CURRENT_PLATFORM}`);
    //         return Promise.resolve(res.data);
    //     } catch (err) {
    //         return Promise.reject(err);
    //     } finally {
    //         this.loading = false;
    //     }
    // };

    // quitClinic = async (reqModel: any, lang: Language = GetApiLang()) => {
    //     this.updating = true;
    //     try {
    //         const res = await Axios.post(`/api/Clinic/Practitioner/Quit?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
    //         return Promise.resolve(res.data);
    //     } catch (err) {
    //         return Promise.reject(err);
    //     } finally {
    //         this.updating = false;
    //     }
    // };

    // responseInviteQuitClinic = async (reqModel: any, isApprove: boolean) => {
    //     this.updating = true;
    //     try {
    //         let apiUrl = isApprove ? `/api/Clinic/Practitioner/Quit/Approve?platformId=${CURRENT_PLATFORM}`
    //             : `/api/Clinic/Practitioner/Quit/Reject?platformId=${CURRENT_PLATFORM}`;
    //         const res = await Axios.put(apiUrl, reqModel);
    //         return Promise.resolve(res.data);
    //     } catch (err) {
    //         return Promise.reject(err);
    //     } finally {
    //         this.updating = false;
    //     }
    // };

    // togglePractitionerStatus = async (reqModel: { clinicId: number, practitionerId: number; }, isActive: boolean) => {
    //     this.updating = true;
    //     try {
    //         const res = await Axios.put(`/api/Clinic/Practitioner/Toggle/Active?isActive=${isActive}&platformId=${CURRENT_PLATFORM}`, reqModel);
    //         return Promise.resolve(res.data);
    //     } catch (err) {
    //         return Promise.reject(err);
    //     } finally {
    //         this.updating = false;
    //     }
    // };

    // closeClinic = async (id: number, lang: Language = GetApiLang()) => {
    //     const userInfo = GetUserProfile();
    //     this.updating = true;
    //     try {
    //         const res = await Axios.put(`/api/Clinic/Close/${id}?lang=${lang}&platformId=${CURRENT_PLATFORM}`);
    //         userInfo.availableClinics = userInfo.availableClinics.filter((c: any) => c.id != id);
    //         if (userInfo.availableClinics.length > 0) userInfo.currentClinicId = userInfo.availableClinics[0].id;
    //         else userInfo.currentClinicId = 0;
    //         SetLocalStorage(USER_INFO, userInfo);
    //         return Promise.resolve(res.data);
    //     } catch (err) {
    //         return Promise.reject(err);
    //     } finally {
    //         this.updating = false;
    //     }
    // };

    // getClinicMemeberInfo = async (clinicId: number, practitionerId: number, lang: Language = GetApiLang(), isloading: boolean = true) => {
    //     if (isloading) this.loading = true;
    //     try {
    //         const res = await Axios.get(`/api/Clinic/Practitioner/GeneralInfo/${clinicId}/${practitionerId}?lang=${lang}&platformId=${CURRENT_PLATFORM}`);
    //         return Promise.resolve(res.data);
    //     } catch (err) {
    //         return Promise.reject(err);
    //     } finally {
    //         if (isloading) this.loading = false;
    //     }
    // };

    // deleteClinicApplication = async (id: number, lang: Language = GetApiLang()) => {
    //     this.deleting = true;
    //     try {
    //         const res = await Axios.delete(`/api/Clinic/${id}?lang=${lang}&platformId=${CURRENT_PLATFORM}`);
    //         return Promise.resolve(res.data);
    //     } catch (err) {
    //         return Promise.reject(err);
    //     } finally {
    //         this.deleting = false;
    //     }
    // };

    // updateClinicOwner = async (reqModel: any) => {
    //     this.updating = true;
    //     try {
    //         const res = await Axios.put(`/api/Clinic/Update/Owner?platformId=${CURRENT_PLATFORM}`, reqModel);
    //         return Promise.resolve(res.data);
    //     } catch (err) {
    //         return Promise.reject(err);
    //     } finally {
    //         this.updating = false;
    //     }
    // };

    // searchModalityByClinicMember = async (practitionerId: number, clinicId: number) => {
    //     this.searchingModality = true;
    //     try {
    //         const res = await Axios.put(`/api/Clinic/Practitioner/Modality/Search/0/0?platformId=${CURRENT_PLATFORM}`, {  practitionerId, clinicId });
    //         return Promise.resolve(res.data);
    //     } catch (err) {
    //         return Promise.reject(err);
    //     } finally {
    //         this.searchingModality = false;
    //     }
    // };

}
